import {
  Button,
  CIActionBtnGroup,
  CITooltip,
  Input,
  MyIcon
} from 'ccs-ui-components';
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { EXCEPTION_ACTION, SERVICE_MODE } from '../../../../../../constants';
import { IIngressExceptionModel } from '../../../../../../models';
import { AppDispatch, useSelector } from '../../../../../../store/AppStore';
import { deleteException, editException, updateException } from '../../../../../../store/reducer/services/ExceptionsReducer';
import { getModeFromUrl, isErrorCodeValid } from '../../../../../../utilities/services/ServiceListUtils';
import { ExceptionActionDropdown } from '../../ExceptionActionDropdown';
import { getActionsErrorMessage } from '../../exceptionUtils';

import classes from './ServiceExceptionItem.module.scss';

interface IServiceExceptionFormProps {
  exceptionId: string;
  errorCode: number;
  action: string[];
  id: number;
  isEditService: boolean;
  itemEdit: boolean;
}

export const ServiceExceptionItem = ({
  exceptionId,
  errorCode,
  action,
  itemEdit,
  id: index,
  isEditService
}: IServiceExceptionFormProps) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [itemExceptionId, setExceptionId] = useState<string>(exceptionId);
  const [itemErrorCode, setErrorCode] = useState<number>(errorCode);
  const [isValid, setIsValid] = useState(true);
  const [errorCodeError, setErrorCodeError] = useState<string>('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [isPresent, setIsPresent] = useState(false);

  // Multiselect states
  const [itemAction, setAction] = useState<string[]>(action);
  const handleData = (args: string[]) => setAction(args);
  const serviceMode = getModeFromUrl();
  const isEditException = (itemEdit && isEditService)
    || (itemEdit && serviceMode !== SERVICE_MODE.VIEW);

  const { exceptions } = useSelector((state) => state.exceptionReducer);

  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );

  const errorCodeChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorCode(parseInt(e.target.value, 10));
  };

  const SERVICE_EXCEPTIONS_ERRORS = {
    duplicateCombinationError: translate(
      'service_exceptions.duplicateCombinationError'
    ),
    errorCodeValue: translate('service_exceptions.errorCodeValue'),
    wrongCombination: translate('service_exceptions.wrongCombination'),
    duplicateErrorCode: translate('service_exceptions.duplicateErrorCode'),
    duplicateExceptionName: translate(
      'service_exceptions.duplicateExceptionName'
    ),
    emptyExceptionSearch: translate('service_exceptions.emptyExceptionSearch')
  };

  const dropDownValue = [
    {
      label: translate('service_exceptions.txt_notify'),
      value: 'Notify'
    },
    {
      label: translate('service_exceptions.txt_compensate'),
      value: 'Compensate'
    },
    {
      label: translate('service_exceptions.txt_ignore'),
      value: 'Ignore'
    },
    {
      label: translate('service_exceptions.txt_compensate_and_recover'),
      value: 'CompensateAndRecover'
    }
  ];

  const onEditHandler = () => {
    if (!itemEdit) {
      dispatch(editException({ index }));
    }
  };

  const onUpdateHandler = () => {
    dispatch(
      updateException({
        exception: {
          exceptionId: itemExceptionId,
          errorCode: itemErrorCode,
          action: itemAction,
          isEdit: false,
          id: index
        },
        index
      })
    );
  };

  const onDeleteHandler = () => {
    dispatch(deleteException({ index }));
  };

  const onCancelHandler = () => {
    setErrorCodeError('');
    dispatch(
      updateException({
        exception: { exceptionId, errorCode, action, isEdit: false, id: index },
        index
      })
    );
    setExceptionId(exceptionId);
    setErrorCode(errorCode);
    setAction(action);
  };

  /**
   * Checks for same exception error added previously
   * @returns true if exists, else false
   */
  const uniqueErrorChecker = () => {
    const obj = exceptions.filter(
      (item) => item.errorCode === itemErrorCode && item.id !== index
    );

    if (!obj.length) {
      setErrorCodeError('');
      return true;
    }
    setErrorCodeError(SERVICE_EXCEPTIONS_ERRORS.duplicateErrorCode);
    return false;
  };

  /**
   * Checks for Compensate/CompensateAndRecover and Ignore not selected together
   * Also checks that Compensate and CompensateAndRecover are not selected together
   * @returns true if valid, else false
   */
  const uniqueActionChecker = () => {
    const actionArray = [...itemAction];
    if (
      (actionArray.includes(EXCEPTION_ACTION.COMPENSATE)
        || actionArray.includes(EXCEPTION_ACTION.COMPENSATE_AND_RECOVER)
        || actionArray.includes(EXCEPTION_ACTION.NOTIFY))
      && actionArray.includes(EXCEPTION_ACTION.IGNORE)
    ) {
      const conflicting = [
        EXCEPTION_ACTION.IGNORE,
        ...actionArray.filter(
          (a) =>
            a === EXCEPTION_ACTION.COMPENSATE
            || a === EXCEPTION_ACTION.COMPENSATE_AND_RECOVER
            || a === EXCEPTION_ACTION.NOTIFY
        )
      ];
      setActionError(getActionsErrorMessage(conflicting, dropDownValue, translate));
      return false;
    }
    if (
      actionArray.includes(EXCEPTION_ACTION.COMPENSATE)
      && actionArray.includes(EXCEPTION_ACTION.COMPENSATE_AND_RECOVER)
    ) {
      setActionError(
        getActionsErrorMessage([
          EXCEPTION_ACTION.COMPENSATE,
          EXCEPTION_ACTION.COMPENSATE_AND_RECOVER
        ], dropDownValue, translate)
      );
      return false;
    }
    setActionError('');
    return true;
  };

  useEffect(() => {
    const data = ingressEventMappingList.map((item) => item.exceptions);
    const value = data[0]?.map(
      (item: IIngressExceptionModel) => item.exceptionId
    );
    const matchedValue = value?.includes(exceptionId);
    if (matchedValue) {
      setIsPresent(true);
    } else {
      setIsPresent(false);
    }
  }, [ingressEventMappingList]);

  useEffect(() => {
    if (
      itemErrorCode
      && itemAction.length > 0
      && uniqueActionChecker()
      && isErrorCodeValid(itemErrorCode)
      && uniqueErrorChecker()
      && (itemErrorCode !== errorCode || itemAction !== action)
    ) {
      setIsValid(false);
    } else setIsValid(true);
  }, [itemErrorCode, itemAction, exceptions]);

  useEffect(() => {
    if (itemErrorCode) {
      if (isErrorCodeValid(itemErrorCode)) {
        setErrorCodeError('');
        uniqueErrorChecker();
      } else {
        setErrorCodeError(SERVICE_EXCEPTIONS_ERRORS.errorCodeValue);
        setIsValid(true);
      }
    } else {
      setErrorCodeError('');
    }
  }, [itemErrorCode]);

  useEffect(() => {
    if (itemAction.length > 0 && itemAction[0]) {
      uniqueActionChecker();
    } else setActionError('');
  }, [itemAction]);

  return (
    <div className={classes['exception-list']}>
      <Row className={classes['list-body']}>
        <Col sm={4} className={classes['list-item']}>
          {isEditException ? (
            <div>
              <Input
                label=""
                name=""
                value={itemExceptionId}
                data-testid="exception-id"
                className={classes['item-exception-form-input']}
                disabled
                aria-label="Exception ID"
              />
            </div>
          ) : (
            <CITooltip placement="right" message={itemExceptionId}>
              <p className={classes['item-exception-form-para']}>
                {itemExceptionId}
              </p>
            </CITooltip>
          )}
        </Col>

        <Col sm={3}>
          {isEditException ? (
            <div>
              <Input
                label=""
                name=""
                onChange={errorCodeChangeHandler}
                value={itemErrorCode || ''}
                data-testid="exception-error-code"
                className={classes['item-exception-form-input']}
                errorTxt={errorCodeError}
                error={errorCodeError.length > 0}
                aria-label="Error Code"
              />
            </div>
          ) : (
            <p
              className={classes['item-exception-form-para']}
              data-testid="excp-item"
            >
              {errorCode}
            </p>
          )}
        </Col>

        <Col sm={3}>
          {isEditException ? (
            <>
              <div className={classes['status-dropdown-on-edit']}>
                <ExceptionActionDropdown
                  options={dropDownValue}
                  onSelect={handleData}
                  selectedOptions={itemAction}
                  height="38px"
                  data-testid="exception-action"
                  placeHolder={String(translate('service_exceptions.plchd_action'))}
                />
              </div>
              {actionError && (
                <div className={classes['error-container']}>{actionError}</div>
              )}
            </>
          ) : (
            <p className={classes['item-exception-form-para']}>
              {itemAction.toString()}
            </p>
          )}
        </Col>
        {!isEditException && serviceMode !== SERVICE_MODE.VIEW && (
          <Col sm={2} className={classes['item-btn-container']}>
            <span>
              <CIActionBtnGroup
                buttons={[
                  {
                    label: 'edit',
                    key: 'editIcon',
                    variant: 'tertiary',
                    className: 'edit-button',
                    disabled: isPresent,
                    onClick: async () => onEditHandler(),
                    testId: 'edit-btn'
                  },
                  {
                    label: 'delete',
                    key: 'deleteIcon',
                    variant: 'tertiary',
                    onClick: async () => onDeleteHandler(),
                    className: 'delete-button-icon',
                    disabled: isPresent,
                    testId: 'delete-btn'
                  }
                ]}
              />
            </span>
          </Col>
        )}
        {isEditException && (
          <Col sm={2}>
            <span className={classes['item-btn-container']}>
              <Button
                className={classes['update-button']}
                onClick={() => onUpdateHandler()}
                disabled={isValid}
                data-testid="update-btn"
                variant="tertiary"
                aria-label="update-btn"
              >
                <MyIcon name="thinCheckIcon" />
              </Button>
              <Button
                className={classes['cancel-button']}
                onClick={() => onCancelHandler()}
                data-testid="cancel-btn"
                variant="tertiary"
                aria-label="cancel-btn"
              >
                <MyIcon
                  name="xMarkLargeIcon"
                  className={classes['cancel-button-icon']}
                />
              </Button>
            </span>
          </Col>
        )}
      </Row>
    </div>
  );
};
